import { Client } from 'src/admin/users/types';
import { CreateFamilyPayload, FamilyMemberWithRelation } from '../hooks/useCreateFamily';
import { Relation } from '../hooks/useFamily';
import { FamilyRole } from '../hooks/useFamilyRoles';

const INVERSE_RELATION: Record<Relation, Relation> = {
  Spouse: 'Spouse',
  Partner: 'Partner',
  Child: 'Parent',
  Parent: 'Child',
  Sibling: 'Sibling',
  Other: 'Other',
};

export function getInverseRelation(relation: Relation): Relation {
  return INVERSE_RELATION[relation];
}

type V2MemberRelationship = Record<string, Relation | null>;

// Role mode: each member maps to at most one family role id (null = unassigned).
export type V2MemberRoles = Record<string, string | null>;

export type InProgressFamilyV2 = {
  selectedMembers: Client[];
  relationships: Record<string, V2MemberRelationship>;
  name: string;
};

export function generateFamilyName(members: Client[]): string {
  if (members.length === 0) return '';
  if (members.length === 1) return `${members[0].firstName} ${members[0].lastName}`;

  const allSameLastName = members.every(m => m.lastName === members[0].lastName);

  if (allSameLastName) {
    const firstNames = members.map(m => m.firstName);
    const allButLast = firstNames.slice(0, -1).join(', ');
    const lastFirst = firstNames[firstNames.length - 1];
    return `${allButLast} & ${lastFirst} ${members[0].lastName}`;
  }

  return `${members[0].firstName} ${members[0].lastName} & ${members[1].firstName} ${members[1].lastName}`;
}

export function getAdvisorUserId(
  members: Client[],
  currentUser: { id?: string } | undefined,
  isSystemAdmin: boolean,
  isServiceAdmin: boolean,
): string {
  if (isSystemAdmin || isServiceAdmin) {
    return members.find(m => m.advisorUserId)?.advisorUserId ?? currentUser?.id ?? '';
  }
  return currentUser?.id ?? '';
}

export function isMemberSelected(client: Client, selectedMembers: Client[]): boolean {
  return selectedMembers.some(m => m.id === client.id);
}

export function starModelPayloadFromInProgressFamilyV2(
  family: InProgressFamilyV2,
  advisorUserId: string,
): CreateFamilyPayload {
  const [primary, ...secondaries] = family.selectedMembers;

  const relationships = Object.entries(family.relationships).flatMap(([fromId, toMap]) =>
    Object.entries(toMap)
      .filter(([, relation]) => relation !== null)
      .map(([toId]) => ({
        fromClientId: fromId,
        toClientId: toId,
        // Backend peer-edge label convention: A→B is labelled with B's relationship TO A
        // (the target's perspective), matching how _assert_star_is_subgraph_of_peer checks edges.
        relationshipType: family.relationships[toId]?.[fromId] as Relation,
      })),
  );

  return {
    name: family.name,
    advisorUserId,
    mainFamilyMember: { clientId: primary.id },
    secondaryFamilyMembers: secondaries.map(
      member =>
        ({
          clientId: member.id,
          relationshipToMainFamilyMember: family.relationships[member.id]?.[primary.id] ?? 'Other',
        }) as FamilyMemberWithRelation,
    ),
    ...(relationships.length > 0 && {
      members: family.selectedMembers.map(m => ({ clientId: m.id })),
    }),
    relationships,
  };
}

export function roleModelPayloadFromInProgressFamilyV2(
  family: { selectedMembers: Client[]; roles: V2MemberRoles; name: string },
  advisorUserId: string,
): CreateFamilyPayload {
  // Role mode owns only the family_member rows and their roles. The backend
  // ignores star/peer fields and pins the main member itself, so we send only
  // `members`. A null role is omitted (the column is nullable).
  return {
    name: family.name,
    advisorUserId,
    members: family.selectedMembers.map(member => {
      const familyRoleId = family.roles[member.id];
      return familyRoleId ? { clientId: member.id, familyRoleId } : { clientId: member.id };
    }),
  };
}

// Relations that have a 1:1 family-role-name equivalent. Any other role name
// (e.g. "Dependant") falls back to "Other" when mapped to a star relationship.
const STAR_RELATIONS: readonly Relation[] = ['Spouse', 'Partner', 'Child', 'Parent', 'Sibling'];

export function roleNameToRelation(roleName: string | undefined): Relation {
  return roleName && STAR_RELATIONS.includes(roleName as Relation)
    ? (roleName as Relation)
    : 'Other';
}

// Flag-off fallback for the role-based UI: the backend still expects the legacy
// star shape, so we pin the first member as main and turn each other member's
// chosen role into their relationship-to-main. The backend derives the inverse
// edges itself from secondary_family_members.
export function starModelPayloadFromRolesV2(
  family: { selectedMembers: Client[]; roles: V2MemberRoles; name: string },
  familyRoles: FamilyRole[],
  advisorUserId: string,
): CreateFamilyPayload {
  const [primary, ...secondaries] = family.selectedMembers;
  const roleNameById = new Map(familyRoles.map(role => [role.id, role.name]));

  return {
    name: family.name,
    advisorUserId,
    mainFamilyMember: { clientId: primary.id },
    secondaryFamilyMembers: secondaries.map(member => {
      const roleId = family.roles[member.id];
      const roleName = roleId ? roleNameById.get(roleId) : undefined;
      return {
        clientId: member.id,
        relationshipToMainFamilyMember: roleNameToRelation(roleName),
      } as FamilyMemberWithRelation;
    }),
  };
}

export function peerModelPayloadFromInProgressFamilyV2(
  family: InProgressFamilyV2,
  advisorUserId: string,
): CreateFamilyPayload {
  const [primary, ...secondaries] = family.selectedMembers;
  return {
    name: family.name,
    advisorUserId,
    members: family.selectedMembers.map(m => ({ clientId: m.id })),
    relationships: secondaries.map(m => ({
      fromClientId: primary.id,
      toClientId: m.id,
      relationshipType: family.relationships[m.id]?.[primary.id] ?? 'Other',
    })),
  };
}
